import {
  Alert,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { ImageInfo, WoList } from "../models/ResponseModels/SearchResponse";
import config from "../config";
import ErrorsString from "../ErrorsString";
import { useEffect, useRef, useState } from "react";
import { GetSearchWoModel } from "../models/GetSearchWoModel";
import InfiniteScroll from "react-infinite-scroller";
import { GetFollowingWoModel } from "../models/GetFollowingWoModel";
import ImageDetailsModal from "./ImageDetailsModal";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloseIcon from "@mui/icons-material/Close";
import WorkOrderCard from "./WorkOrderCard";
import ImageCard from "./ImageCard";

interface Props {
  contextPage: string;
  keyword?: string;
  contentType?: string;
  channelIds?: string;
}

export default function WorkorderCards(props: Props) {
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [workorderList, setWorkorderList] = useState<WoList[]>([]);
  const [imagesList, setImagesList] = useState<ImageInfo[]>([]);
  const [nextUuid, setNextUuid] = useState<string>("");
  const [hasMoreWorkorders, setHasMoreWorkorders] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [initialLoad, setInitialLoad] = useState<boolean>(true);
  const [message, setMessage] = useState<string | null>(null);
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);
  let [alertShareMessage, setAlertShareMessage] = useState<string>("");
  let [shareId, setShareId] = useState<number | null>(null);
  let [showAlertShare, setShowAlertShare] = useState<boolean>(false);
  const [isAlertVisible, setIsAlertVisible] = useState<boolean>(true);
  let [openModal, setOpenModal] = useState<boolean>(false);
  const [openDialogShareLink, setOpenDialogShareLink] = useState(false);
  let [imageUrlShare, setImageUrlShare] = useState<string>("");
  const textRefLink = useRef(null);
  let [isCopied, setIsCopied] = useState<boolean>(false);

  const baseUrl = window.location.origin;

  useEffect(() => {
    setIsAlertVisible(false);
    setMessage(null);
    setWorkorderList([]);
    setImagesList([]);
    setCurrentPage(0);
    setInitialLoad(true);
    setHasMoreWorkorders(true);
  }, [props.keyword, props.contentType]);

  const handleDialogShareLinkClose = () => {
    setShowAlertShare(false);
    setAlertShareMessage("");
    setImageUrlShare("");
    setOpenDialogShareLink(false);
  };

  function copyLinkToClipboard() {
    if (textRefLink.current) {
      navigator.clipboard
        .writeText(textRefLink.current.querySelector("#shared-url").textContent)
        .then(() => {
          setIsCopied(true);
        })
        .catch((err) => {
          console.error("Failed to copy text: ", err);
        });
    }
  }

  function searchRequest() {
    if (loading) return;
    let response = null;
    let responseData = null;
    let statusCode = null;
    let hasMore = true;

    setLoading(true);

    console.log("Starting API call GetSearchWoModel...");

    (async () => {
      response = await GetSearchWoModel(
        props.keyword,
        props.contentType,
        props.channelIds,
        config.searchPageSize,
        currentPage
      );
      //console.log("GetSearchWoModel response: " + JSON.stringify(response));

      responseData = response?.data;
      statusCode = response?.status;

      if (responseData === undefined) {
        setMessage(ErrorsString.errorMessageGeneric);
        setIsAlertVisible(!isAlertVisible);
        setHasMoreWorkorders(false);
        setLoading(false);

        return;
      }

      hasMore = _setSearchResponse(statusCode, responseData, hasMore);

      setTimeout(() => {
        setLoading(false);
      }, 2000);

      return;
    })();
  }

  function _setSearchResponse(
    statusCode: any,
    responseData: any,
    hasMore: boolean
  ) {
    if (statusCode == 200) {
      if (responseData.code === "0") {
        setMessage(ErrorsString.noItemsFound);
        setIsAlertVisible(!isAlertVisible);
        setHasMoreWorkorders(false);
      } else if (responseData.code === "1") {
        hasMore = _setSearchSuccessResponse(responseData, hasMore);
      } else {
        const message =
          responseData?.message && responseData.message !== ""
            ? responseData.message
            : "An errore occured while searching";
        setMessage(message);
        setIsAlertVisible(!isAlertVisible);
        setHasMoreWorkorders(false);
      }
    } else {
      setMessage(ErrorsString.errorMessageGeneric);
      setIsAlertVisible(!isAlertVisible);
      setHasMoreWorkorders(false);
    }
    return hasMore;
  }

  function _setSearchSuccessResponse(responseData: any, hasMore: boolean) {
    if (responseData.workorderInfo.length > 0) {
      setWorkorderList([...workorderList, ...responseData.workorderInfo]);
    }
    if (responseData.imageInfoList.length > 0) {
      setImagesList([...imagesList, ...responseData.imageInfoList]);
    }

    const totalPages = responseData.totalPages - 1;
    hasMore = currentPage == totalPages ? false : true;
    setHasMoreWorkorders(hasMore);
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);

    /*console.log("current page: " + currentPage);
    console.log("total pages: " + totalPages);
    console.log("has more pages: " + hasMore);
    console.log("next page: " + nextPage);*/
    return hasMore;
  }

  function followRequest() {
    if (loading) return;
    let response = null;
    let responseData = null;
    let statusCode = null;
    let hasMore = true;

    setLoading(true);

    console.log("Starting API call S...");
    (async () => {
      response = await GetFollowingWoModel(config.searchPageSize, nextUuid);
      //console.log("GetFollowingWoModel response: " + JSON.stringify(response));

      responseData = response?.data;
      statusCode = response?.status;
      let nextPageKey = null;

      if (responseData === undefined) {
        setMessage(ErrorsString.errorMessageGeneric);
        setIsAlertVisible(!isAlertVisible);
        setHasMoreWorkorders(false);
        setLoading(false);

        return;
      }

      ({ nextPageKey, hasMore } = _setFollowResponse(
        statusCode,
        responseData,
        nextPageKey,
        hasMore
      ));
      setLoading(false);
      return;
    })();
  }

  function _setFollowResponse(
    statusCode: any,
    responseData: any,
    nextPageKey: any,
    hasMore: boolean
  ) {
    if (statusCode == 200) {
      if (responseData.code === "1") {
        setWorkorderList([...workorderList, ...responseData.workorderInfo]);
        nextPageKey = responseData.nextPageKey;
        setNextUuid(nextPageKey);
        hasMore = nextPageKey === "" || nextPageKey === null ? false : true;
        setHasMoreWorkorders(hasMore);
      } else if (responseData.code === "0") {
        setMessage("No items followed");
        setIsAlertVisible(!isAlertVisible);
        setHasMoreWorkorders(false);
      } else if (responseData.code === "-1") {
        const errorMessage = responseData ? responseData.message : "";
        setMessage(
          "An error occurred while loading following contents. Error: " +
            errorMessage
        );
        setIsAlertVisible(!isAlertVisible);
        setHasMoreWorkorders(false);
      }
    } else {
      setMessage(ErrorsString.errorMessageGeneric);
      setIsAlertVisible(!isAlertVisible);
      setHasMoreWorkorders(false);
    }
    return { nextPageKey, hasMore };
  }

  function getMoreWorkorders() {
    if (initialLoad) {
      setInitialLoad(false);
    }

    if (props.contextPage === "search") {
      //esegui API per contenuti cercati
      searchRequest();
    } else if (props.contextPage === "follow") {
      //esegui API per contenuti seguiti
      followRequest();
    }
  }

  const ImageSharedUrlDialog = () => {
    return (
      <Dialog
        onClose={handleDialogShareLinkClose}
        key={"shared_url_modal"}
        open={openDialogShareLink}
        fullWidth={true} // Permette di usare tutta la larghezza disponibile
        sx={{
          "& .MuiDialog-paper": {
            position: "absolute", // Posizione assoluta per personalizzare il posizionamento
            top: "10px", // Posiziona la modale esattamente in cima allo schermo
            margin: 0, // Rimuove i margini
            width: "99%", // Imposta la larghezza al 99%
            maxWidth: "none", // Disabilita la larghezza massima predefinita
          },
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>
          Link to share
          <IconButton
            aria-label="close"
            onClick={handleDialogShareLinkClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {showAlertShare ? (
              <Alert severity="error">{alertShareMessage}</Alert>
            ) : (
              <div
                ref={textRefLink}
                onClick={copyLinkToClipboard}
                className=" font-skyRegular p-2 bg-blue-100 border border-blue-300 rounded-md cursor-pointer hover:bg-blue-200 transition-colors break-words"
                title="Click to copy url"
              >
                <ContentCopyIcon className="mr-4" />
                <span id="shared-url">{imageUrlShare}</span>

                <span
                  className={` float-right text-blue-500 font-skyBold  ${
                    isCopied ? "block" : "hidden"
                  } `}
                >
                  copied
                </span>
              </div>
            )}
          </DialogContentText>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <>
      {message === null ? (
        <InfiniteScroll
          pageStart={0}
          loadMore={getMoreWorkorders}
          hasMore={hasMoreWorkorders}
          threshold={200} // triggers loading 200px before reaching the bottom
        >
          {workorderList.length > 0 || imagesList.length > 0 ? (
            <div
              key="card-box"
              className="mt-5 mb-5 grid grid-rows-2 grid-cols-[repeat(auto-fill,minmax(310px,1fr))] gap-12 "
            >
              {workorderList.length > 0 &&
                workorderList.map((item, index) => (
                  <WorkOrderCard item={item} index={index} />
                ))}

              {imagesList.length > 0 &&
                imagesList.map((item, index) => (
                  <ImageCard
                    item={item}
                    index={index}
                    baseUrl={baseUrl}
                    setSelectedUuid={setSelectedUuid}
                    setOpenModal={setOpenModal}
                    setShareId={setShareId}
                    setShowAlertShare={setShowAlertShare}
                    setAlertShareMessage={setAlertShareMessage}
                    setOpenDialogShareLink={setOpenDialogShareLink}
                    setIsCopied={setIsCopied}
                    setImageUrlShare={setImageUrlShare}
                  />
                ))}

              <ImageSharedUrlDialog />
              <ImageDetailsModal
                key={"image_details_modal"}
                openModal={openModal}
                setOpenModal={setOpenModal}
                uuid={selectedUuid}
                setSelectedUuid={setSelectedUuid}
              ></ImageDetailsModal>
            </div>
          ) : (
            <Alert
              severity="error"
              style={{
                display:
                  isAlertVisible && !initialLoad && !loading ? "flex" : "none",
              }}
              className="mt-5 mx-5"
            >
              {ErrorsString.noItemsFound}
            </Alert>
          )}
          {loading && (
            <div
              key="loader"
              className="flex items-start justify-center h-1/2 pt-5"
            >
              <CircularProgress />
            </div>
          )}
        </InfiniteScroll>
      ) : (
        <Alert severity="error" className="mt-5 mx-5">
          {message}
        </Alert>
      )}
    </>
  );
}
